import { useFetchPets } from 'api/queries/useFetchPets'
import { useFetchProductSubscriptions } from 'api/queries/useFetchProductSubscriptions'
import { useTranslation } from 'hooks/useTranslation'
import { useRouter } from 'hooks/useRouter'
import { useEffect, useMemo } from 'react'
import { getClientId } from 'utils/getClientId'
import { ENV } from '../../../../../constants/EnvHelp'

export const useMyAccountMenu = () => {
  const { t } = useTranslation()
  const router = useRouter()

  const { data: pets } = useFetchPets()
  const { data: subscriptions } = useFetchProductSubscriptions()

  const clientId = getClientId() || ''

  const activeSection = useMemo(() => {
    if (router.pathname.includes('pets')) {
      return t('my-account:menu.pets.title')
    }
    if (router.pathname.includes('bulk')) {
      return t('my-account:menu.bulk.title')
    }
    if (router.pathname.includes('orders')) {
      return t('my-account:menu.orders.title')
    }
    if (router.pathname.includes('[clientId]')) {
      return t('my-account:menu.settings.title')
    }
    return t('my-account:menu-title')
  }, [router.pathname, t])

  const PETS_ITEMS = useMemo(
    () =>
      pets?.map(({ name: firstName, petId }) => ({
        label: firstName,
        url: `/user/pets/${petId}`,
      })),
    [pets]
  )

  const BULK_SUBSCRIPTIONS_ITEMS = useMemo(() => {
    return subscriptions?.subscriptionIds.map((subscriptionId, index) => ({
      label: `Bulk subscription ${index + 1}`,
      url: `/user/bulk-subscriptions/${subscriptionId}`,
    }))
  }, [subscriptions])

  const mealPlanUrl = useMemo(() => {
    let mealPlanUrl
    if (PETS_ITEMS?.length && PETS_ITEMS?.length == 2) {
      mealPlanUrl = '/meal-plan?promoCode=3RDPET25'
    } else if (PETS_ITEMS?.length && PETS_ITEMS?.length == 1) {
      mealPlanUrl = '/meal-plan?promoCode=2NDPET25'
    } else {
      mealPlanUrl = '/meal-plan'
    }
    return mealPlanUrl
  }, [PETS_ITEMS])

  const PETS = {
    title: t('my-account:menu.pets.title'),
    items: PETS_ITEMS,
    cta: {
      label: t('my-account:menu.pets.cta'),
      url: mealPlanUrl,
    },
  }

  const BULK = useMemo(
    () => ({
      title: t('my-account:menu.bulk.title'),
      items: BULK_SUBSCRIPTIONS_ITEMS,
      cta: {
        label: t('my-account:menu.bulk.cta'),
        url: '/products',
      },
    }),
    [t, BULK_SUBSCRIPTIONS_ITEMS]
  )

  const ORDERS = useMemo(
    () => ({
      title: t('my-account:menu.orders.title'),
      items: [
        { label: 'Current Orders', url: '/user/orders/current' },
        { label: t('my-account:menu.orders.history'), url: '/user/orders/history' },
      ],
    }),
    [t]
  )

  const ACCOUNT_SETTINGS = useMemo(
    () => ({
      title: t('my-account:menu.settings.title'),
      items: [
        {
          label: t('my-account:menu.settings.profile'),
          url: `/user/${clientId}`,
        },
        {
          label: 'My Rewards',
          url: `/user/${clientId}/rewards`,
        },
      ],
    }),
    [clientId, t]
  )

  return {
    menuSections: [PETS, BULK, ORDERS, ACCOUNT_SETTINGS],
    activeSection,
    mealPlanUrl,
  }
}
